import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getPersonalityLabels, getPractitionerTypeMap } from "../../../services/utils";
import { useInfiniteQuery } from "@tanstack/react-query";
import { api } from "../../../services/api";
import React, { useMemo, useEffect, useRef } from "react";
import { useReports } from "../../praxis-play-dashboard/context/reports-context";
import { useApp } from "../../../services/app-context";
import { CallCard } from "./CallCard";
import { ListActionBar } from "./ListActionBar";

dayjs.extend(utc);
dayjs.extend(timezone);

function HistoryListInner({
    filter,
    onRowClick,
    enablePagination = false,
    sort = "newest",
    onSortChange
}: {
    filter?: {
        practitionerType?: string;
        sortBy?: string;
        month?: string;
        year?: string;
    };
    onRowClick: (row: any) => void;
    enablePagination?: boolean;
    sort?: string;
    onSortChange?: (value: string) => void;
}) {
    const {
        userReports: previewReports,
        isLoadingReports: isLoadingPreview,
    } = useReports();
    const { tenantProfile } = useApp();
    const sentinelRef = useRef<HTMLDivElement>(null);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const practitionerTypeMap = useMemo(
        () => getPractitionerTypeMap(tenantProfile),
        [tenantProfile]
    );

    const {
        data: paginatedData,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading: isLoadingPaginated,
    } = useInfiniteQuery({
        queryKey: ["paginated-reports-table", filter, sort],
        queryFn: ({ pageParam }) => api.getAllReports(pageParam, { ...filter, sortBy: sort }),
        getNextPageParam: (lastPage) => {
            return lastPage?.data?.links?.after || undefined;
        },
        enabled: enablePagination,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        cacheTime: 5 * 60 * 1000,
    });

    const metaData = useMemo(() => {
        return paginatedData?.pages?.[0]?.data?.metadata || {};
    }, [paginatedData]);

    const allReports = useMemo(() => {
        if (enablePagination && paginatedData?.pages) {
            return paginatedData.pages.flatMap((page) => page?.data?.data || []);
        }
        return [];
    }, [enablePagination, paginatedData]);

    useEffect(() => {
        if (!enablePagination || !sentinelRef.current || !scrollContainerRef.current)
            return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
                    fetchNextPage();
                }
            },
            {
                root: scrollContainerRef.current,
                rootMargin: "200px",
                threshold: 0.1,
            }
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [enablePagination, hasNextPage, isFetchingNextPage, fetchNextPage]);

    const reports = enablePagination ? allReports : previewReports;

    const isLoading = enablePagination ? isLoadingPaginated : isLoadingPreview;

    if (isLoading) {
        return (
            <div className="w-full flex flex-col gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                    <div
                        key={i}
                        className="w-full p-[14px] bg-white rounded-[12px] border-[0.5px] border-gray-100 animate-pulse"
                    >
                        <div className="h-4 bg-gray-201 rounded w-1/3 mb-3" />
                        <div className="h-5 bg-gray-201 rounded w-1/2 mb-2" />
                        <div className="h-4 bg-gray-201 rounded w-1/4" />
                    </div>
                ))}
            </div>
        );
    }

    return (
        <>
            <ListActionBar count={metaData?.total ?? 0} onSortChange={(value) => onSortChange?.(value)} sort={sort} />

            <div ref={scrollContainerRef} className="w-full flex-1 overflow-auto flex flex-col gap-3">
                {reports.map((session: any, index: number) => {
                    const isClickable = !session?.endReason && !session?.end_reason;
                    return (
                        <CallCard
                            key={session?.sessionID || index}
                            session={session}
                            practitionerTypeLabel={
                                practitionerTypeMap[session.practitionerType] ||
                                session.practitionerType
                            }
                            personalityLabels={getPersonalityLabels(session.personalityType, tenantProfile)}
                            onClick={() => onRowClick(session)}
                            clickable={isClickable}
                            testId="history-row"
                        />
                    );
                })}

                {hasNextPage && (
                    <div ref={sentinelRef} className="h-px w-full" />
                )}

                {isFetchingNextPage && (
                    <div className="text-center py-2">
                        <span className="text-text-gray text-sm">Loading more...</span>
                    </div>
                )}

                {!isLoading && reports.length === 0 && (
                    <div className="text-center py-8 text-text-gray text-sm">
                        No sessions found
                    </div>
                )}
            </div>
        </>
    );
}

export const HistoryList = React.memo(HistoryListInner);
